import { useTable } from '@refinedev/antd';
import { Table, Tag, Input } from 'antd';
import { Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { FC } from 'react';

interface UserRecord {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organization?: { name: string };
  feedbackCount: number;
  lastLoginAt: string;
}

const ROLE_COLORS: Record<string, { color: string; text: string }> = {
  ROLE_ADMIN: { color: 'blue', text: 'Admin' },
  ROLE_SALES: { color: 'purple', text: 'Commercial' },
  ROLE_MEMBER: { color: 'default', text: 'Membre' },
  ROLE_OWNER: { color: 'gold', text: 'Owner' },
  ROLE_MANAGER: { color: 'cyan', text: 'Manager' },
  ROLE_USER: { color: 'default', text: 'Utilisateur' },
};

const UserListPage: FC = () => {
  const navigate = useNavigate();

  const { tableProps, searchFormProps } = useTable<UserRecord>({
    resource: 'admin/users',
    pagination: { pageSize: 20 },
    onSearch: (values) => {
      const params = values as { search?: string };
      return [{ field: 'search', operator: 'contains', value: params.search }];
    },
  });

  return (
    <div className="space-y-6">
      {/* Header gradient */}
      <div className="fc-gradient-header rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6" />
          <div>
            <h1 className="text-xl font-bold">Annuaire Clients</h1>
            <p className="mt-1 text-sm text-blue-100">Gestion des utilisateurs de la plateforme</p>
          </div>
        </div>
      </div>

      {/* Search + Table */}
      <div className="fc-card overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Utilisateurs</h3>
          <form {...searchFormProps}>
            <Input.Search
              placeholder="Rechercher par email, nom..."
              allowClear
              onSearch={(value) => searchFormProps?.onFinish?.({ search: value })}
              className="w-72"
            />
          </form>
        </div>
        <Table
          {...tableProps}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => navigate(`/support/users/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        >
          <Table.Column dataIndex="email" title="Email" />
          <Table.Column dataIndex="firstName" title="Prénom" />
          <Table.Column dataIndex="lastName" title="Nom" />
          <Table.Column
            dataIndex="role"
            title="Rôle"
            render={(role: string) => {
              const cfg = ROLE_COLORS[role] ?? { color: 'default', text: role };
              return <Tag color={cfg.color}>{cfg.text}</Tag>;
            }}
          />
          <Table.Column
            dataIndex={['organization', 'name']}
            title="Organisation"
            render={(name: string) => (
              <span className="text-slate-700 dark:text-slate-300">{name ?? '—'}</span>
            )}
          />
          <Table.Column dataIndex="feedbackCount" title="Feedbacks" />
          <Table.Column
            dataIndex="lastLoginAt"
            title="Dernière connexion"
            render={(date: string) => (
              <span className="text-slate-500 dark:text-slate-400">
                {date ? new Date(date).toLocaleDateString('fr-FR') : '—'}
              </span>
            )}
          />
        </Table>
      </div>
    </div>
  );
};

export default UserListPage;
